import { SocketEventType } from "@swivl-mono/types";
import { useEffect } from "react";

import BotPresenceManager from "../../Models/ContactCenterV3/BotPresence.Manager";
import ContactCenterV3Model from "../../Models/ContactCenterV3/ContactCenterV3.Store";
import { isNotificationLeader, startNotificationLeader, stopNotificationLeader } from "../../Models/ContactCenterV3/NotificationLeader";
import { Member } from "../../Models/Member/Member.Current";
import MembersModel from "../../Models/Member/Members.Model";
import { AssignmentToast } from "../../Views/ContactCenterV3/Conversations/AssignmentToast";

/** How often the keepalive fires. Presence TTL is 90s server-side. */
const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Keeps an available agent routable, and lets them accept an escalation, from
 * ANY page in Studio (ENG-3098).
 *
 * Both of these used to live inside `ConversationsPage`'s mount effect, so an
 * agent who turned availability on and then walked to Ticketing stopped
 * heartbeating, expired out of Redis presence ~90s later, and silently dropped
 * out of `RoutingEngine`'s candidate pool — while the header toggle still said
 * they were available. Even had an offer reached them, no toast was mounted to
 * accept it from.
 *
 * Mounted in `App` beside `ToastRoot` and `VoiceCallBar`: for the whole session,
 * on every route, and inside `BrowserRouter` so the toast's accept can navigate.
 *
 * Deliberately NOT owned here: everything else in `ConversationsPage`'s mount —
 * voice-device init, the start-shift modal, `loadConversations`, the
 * conversation-message sinks, and the coaching subscriptions are all that page's
 * concerns and stay there.
 */
export function CCPresenceHost(): React.ReactElement | null {
  // Subscribed, not read once: toggling availability must start or stop the
  // heartbeat immediately, not on the next navigation.
  const isAvailable = MembersModel.useState((s) => s.isAvailable);

  // ── The connection, the offer subscription, and the one-tab lease ──────────
  useEffect(() => {
    if (!Member.orgId || !Member.id) return undefined;

    BotPresenceManager.connect();
    // The assignment toast is app-wide, so the softphone must be registered
    // app-wide too — not only once the Conversations page has mounted. An
    // Accept from any other page dialed an identity nothing was listening on
    // (ENG-3448 test, 2026-09-29). init() is idempotent and costs no microphone.
    void import("../../Models/ContactCenterV3/CCVoiceDevice").then(({ CCVoiceDevice }) => CCVoiceDevice.init()).catch(() => {});
    // R9 (ENG-2827): exactly one tab plays the escalation sound and raises the
    // browser notification. The lease lives here rather than on the Conversations
    // page because the toast it gates is now app-wide — left behind, an agent
    // outside the CC would get a silent toast.
    startNotificationLeader();

    /**
     * Offer lifecycle only. `ConversationsPage` keeps its own, fuller
     * CC_ASSIGNMENT handler for list refreshes and pull-takeover notices; the
     * toast has a single owner so it cannot be raised twice.
     *
     * Receiving this event at all means the offer is for us —
     * `broadcastCCAssignment` targets the assigned agent's per-member room.
     */
    const handleAssignment = (data: { payload?: Record<string, unknown> }): void => {
      const payload = data?.payload;
      if (!payload) return;

      if (payload.action === "offered" && payload.assignmentId) {
        ContactCenterV3Model.showAssignmentToast({
          assignmentId: payload.assignmentId as number,
          userName: (payload.userName as string) ?? `User ${payload.userId}`,
          channel: (payload.channel as string) ?? "web",
          reason: payload.escalationReason as string | undefined,
          expiresAt: payload.expiresAt as string | undefined,
          userId: payload.userId as number,
          botConversationId: payload.botConversationId as number | undefined
        });
      }

      // The caller hung up while the offer was ringing — kill the toast (and the
      // alert sound) rather than letting a dead call ring out (ENG-2929).
      if (payload.action === "withdrawn" && payload.assignmentId) {
        const current = ContactCenterV3Model.state.pendingAssignmentToast;
        if (current?.assignmentId === payload.assignmentId) ContactCenterV3Model.dismissAssignmentToast();
      }
    };

    /**
     * A manager set THIS agent offline from the Agent Dashboard (ENG-3052).
     *
     * App-wide for the same reason as the offer toast: the agent could be
     * anywhere in Studio when it happens, and previously this only fired on the
     * Conversations page.
     *
     * `setByMemberId` is what separates a manager action from an ordinary
     * presence tick — without it every agent would be toasted on every
     * heartbeat transition.
     */
    const handlePresence = (data: { payload?: Record<string, unknown> }): void => {
      const payload = data?.payload;
      if (!payload || payload.memberId !== Member.id) return;
      const setBy = payload.setByMemberId as number | undefined;
      if (setBy == null || setBy === Member.id) return;

      // Turn our own availability off locally and on the server. Without this
      // the manager's action cannot stick: this tab heartbeats every 30s while
      // it believes it is available, and it periodically re-PUTs
      // availability=true — either one would undo the manager within seconds.
      void MembersModel.setAvailability(false);

      const message = "Your manager set your status to Offline.";
      // The CC's own bottom-right stack, not react-toastify's top-right
      // container — same place the escalation offer appears.
      ContactCenterV3Model.showPresenceNotice(message);
      try {
        if (isNotificationLeader() && typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("Status changed", { body: message, tag: "presence-set-by-manager" });
        }
      } catch {
        // a blocked Notification constructor must never break presence handling
      }
    };

    BotPresenceManager.on(SocketEventType.CC_ASSIGNMENT, handleAssignment);
    BotPresenceManager.on(SocketEventType.CC_PRESENCE, handlePresence);

    // An offer that arrived while the socket was down is still live server-side
    // (its expiresAt is authoritative), so recover it on connect and reconnect.
    void ContactCenterV3Model.fetchPendingOffers();
    const unsubscribeReconnect = BotPresenceManager.onReconnect(() => {
      void ContactCenterV3Model.fetchPendingOffers();
    });

    return () => {
      BotPresenceManager.off(SocketEventType.CC_ASSIGNMENT, handleAssignment);
      BotPresenceManager.off(SocketEventType.CC_PRESENCE, handlePresence);
      unsubscribeReconnect();
      stopNotificationLeader();
      // The socket is intentionally left connected: this host lives as long as
      // the session, so the only thing that unmounts it is logout or a reload.
    };
  }, []);

  // ── The keepalive ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!Member.orgId || !Member.id) return undefined;
    // Availability off means routable off. The toggle stays the single source of
    // truth — including when a manager sets this agent offline from the Agent
    // Dashboard, which this must not immediately undo.
    if (!isAvailable) return undefined;

    // Real interaction resets the idle clock (ENG-3098). Before this, the ONLY
    // thing that counted as activity anywhere in Studio was sending a message in
    // a conversation — so any agent who hadn't typed in five minutes read as
    // idle while sitting right there working.
    //
    // Listeners only set a flag; they never call the server. Activity is
    // reported at most once per tick, piggybacking the heartbeat that already
    // fires every 30s — because each report costs a DB count plus a Redis
    // round-trip, and wiring that straight to mousemove would be a firehose at
    // hundreds of agents per org. Thirty-second resolution against a five-minute
    // threshold is far more precision than the threshold needs.
    let sawInput = false;
    const noteInput = (): void => {
      sawInput = true;
    };
    const INPUT_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"] as const;
    // `passive` so scroll/touch listeners can never delay paint.
    for (const evt of INPUT_EVENTS) window.addEventListener(evt, noteInput, { passive: true });

    const fire = (): void => {
      if (!MembersModel.useState.getState().isAvailable) return;
      BotPresenceManager.heartbeat();
      if (sawInput) {
        sawInput = false;
        // Resets the idle timer and pulls idle → online/busy atomically.
        BotPresenceManager.recordActivity();
      }
    };
    fire();

    // ENG-2826 §8.10: page timers are throttled in background tabs, so
    // heartbeats drift and presence flaps. A Worker timer is exempt. That
    // mattered when this ran on one page; it matters more now it has to survive
    // a tab sitting in the background all afternoon. Falls back to an interval
    // where Workers are unavailable.
    let worker: Worker | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    try {
      const src = `setInterval(() => postMessage("tick"), ${HEARTBEAT_INTERVAL_MS});`;
      worker = new Worker(URL.createObjectURL(new Blob([src], { type: "application/javascript" })));
      worker.onmessage = fire;
    } catch {
      interval = setInterval(fire, HEARTBEAT_INTERVAL_MS);
    }

    return () => {
      for (const evt of INPUT_EVENTS) window.removeEventListener(evt, noteInput);
      worker?.terminate();
      if (interval) clearInterval(interval);
    };
  }, [isAvailable]);

  return <AssignmentToast />;
}
